// Evidence Capture Component
// Photo and signature capture for task execution

import React, { useState, useRef } from 'react';
import { Camera, X, Check, RotateCcw } from 'lucide-react';
import { evidenceService } from '../../services/evidenceService';
import Modal from '../ui/Modal';
import './EvidenceCapture.css';

const EvidenceCapture = ({ 
  isOpen, 
  onClose, 
  taskId, 
  stepId, 
  actionId, 
  executionId,
  executorId,
  executorName,
  onEvidenceCaptured 
}) => {
  const [evidenceType, setEvidenceType] = useState('during'); // 'before', 'during', 'after'
  const [captureMode, setCaptureMode] = useState('photo'); // 'photo', 'signature'
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [notes, setNotes] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [error, setError] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const isDrawing = useRef(false);
  
  // Initialize camera when modal opens
  React.useEffect(() => {
    if (isOpen && captureMode === 'photo') {
      initializeCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [isOpen, captureMode]);
  
  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Prefer back camera on mobile
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please use file upload instead.');
    }
  };
  
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };
  
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg');
      setPhotoPreview(dataUrl);
      stopCamera();
    }
  };
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const startSignature = () => {
    if (!signatureCanvasRef.current) return;
    
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setSignatureDataUrl(null);
    isDrawing.current = false;
  };
  
  const handleSignatureMouseDown = (e) => {
    isDrawing.current = true;
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };
  
  const handleSignatureMouseMove = (e) => {
    if (!isDrawing.current) return;
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };
  
  const handleSignatureMouseUp = () => {
    if (isDrawing.current) {
      isDrawing.current = false;
      const canvas = signatureCanvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      setSignatureDataUrl(dataUrl);
    }
  };
  
  const handleSave = async () => {
    try {
      setIsCapturing(true);
      setError(null);
      
      let evidence;
      
      if (captureMode === 'photo') {
        if (!photoPreview) {
          setError('Please capture or upload a photo');
          setIsCapturing(false);
          return;
        }
        
        // Convert data URL to blob
        const response = await fetch(photoPreview);
        const blob = await response.blob();
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        
        evidence = await evidenceService.capturePhoto(
          taskId,
          stepId,
          actionId,
          file,
          evidenceType,
          {
            executionId,
            executorId,
            executorName,
            notes: notes.trim() || null,
            location: null // Could get from GPS if needed
          }
        );
      } else {
        if (!signatureDataUrl) {
          setError('Please provide a signature');
          setIsCapturing(false);
          return;
        }
        
        evidence = await evidenceService.captureSignature(
          taskId,
          executorId,
          signatureDataUrl,
          {
            executionId,
            executorName,
            notes: notes.trim() || null
          }
        );
      }
      
      if (onEvidenceCaptured) {
        onEvidenceCaptured(evidence);
      }
      
      handleClose();
    } catch (err) {
      console.error('Error capturing evidence:', err);
      setError(err.message || 'Failed to capture evidence');
    } finally {
      setIsCapturing(false);
    }
  };
  
  const handleClose = () => {
    setPhotoPreview(null);
    setSignatureDataUrl(null);
    setNotes('');
    setError(null);
    stopCamera();
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Capture Evidence: ${captureMode === 'photo' ? 'Photo' : 'Signature'}`}
      size="large"
    >
      <div className="evidence-capture">
        {error && (
          <div className="error-message">{error}</div>
        )}
        
        {/* Capture Mode Toggle */}
        <div className="capture-mode-toggle">
          <button
            className={`btn ${captureMode === 'photo' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setCaptureMode('photo')}
          >
            <Camera size={16} /> Photo
          </button>
          <button
            className={`btn ${captureMode === 'signature' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setCaptureMode('signature')}
          >
            Signature
          </button>
        </div>
        
        {/* Photo Capture */}
        {captureMode === 'photo' && (
          <div className="photo-capture">
            {/* Evidence Type Selection */}
            <div className="evidence-type-selection">
              <label>Evidence Type:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    value="before"
                    checked={evidenceType === 'before'}
                    onChange={(e) => setEvidenceType(e.target.value)}
                  />
                  Before
                </label>
                <label>
                  <input
                    type="radio"
                    value="during"
                    checked={evidenceType === 'during'}
                    onChange={(e) => setEvidenceType(e.target.value)}
                  />
                  During
                </label>
                <label>
                  <input
                    type="radio"
                    value="after"
                    checked={evidenceType === 'after'}
                    onChange={(e) => setEvidenceType(e.target.value)}
                  />
                  After
                </label>
              </div>
            </div>
            
            {/* Camera Preview */}
            {!photoPreview && cameraStream && (
              <div className="camera-preview">
                <video ref={videoRef} autoPlay playsInline className="video-preview" />
                <div className="camera-controls">
                  <button className="btn btn-primary" onClick={capturePhoto}>
                    <Camera size={20} /> Capture Photo
                  </button>
                  <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                    Upload Photo
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            
            {/* File Upload Fallback */}
            {!photoPreview && !cameraStream && (
              <div className="file-upload">
                <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
                  <Camera size={20} /> Upload Photo
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            
            {/* Photo Preview */}
            {photoPreview && (
              <div className="photo-preview">
                <img src={photoPreview} alt="Captured photo" />
                <button className="btn btn-secondary" onClick={() => {
                  setPhotoPreview(null);
                  initializeCamera();
                }}>
                  <RotateCcw size={16} /> Retake
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Signature Capture */}
        {captureMode === 'signature' && (
          <div className="signature-capture">
            <div className="signature-canvas-container">
              <canvas
                ref={signatureCanvasRef}
                width={500}
                height={200}
                className="signature-canvas"
                onMouseDown={handleSignatureMouseDown}
                onMouseMove={handleSignatureMouseMove}
                onMouseUp={handleSignatureMouseUp}
                onMouseLeave={handleSignatureMouseUp}
                onTouchStart={(e) => {
                  e.preventDefault();
                  const touch = e.touches[0];
                  const mouseEvent = new MouseEvent('mousedown', {
                    clientX: touch.clientX,
                    clientY: touch.clientY
                  });
                  signatureCanvasRef.current.dispatchEvent(mouseEvent);
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  const touch = e.touches[0];
                  const mouseEvent = new MouseEvent('mousemove', {
                    clientX: touch.clientX,
                    clientY: touch.clientY
                  });
                  signatureCanvasRef.current.dispatchEvent(mouseEvent);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSignatureMouseUp();
                }}
              />
            </div>
            <button className="btn btn-secondary" onClick={startSignature}>
              Clear Signature
            </button>
          </div>
        )}
        
        {/* Notes */}
        <div className="evidence-notes">
          <label>Notes (optional):</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this evidence..."
            rows={3}
          />
        </div>
        
        {/* Actions */}
        <div className="evidence-actions">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isCapturing}>
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={isCapturing || (captureMode === 'photo' && !photoPreview) || (captureMode === 'signature' && !signatureDataUrl)}
          >
            {isCapturing ? 'Saving...' : <><Check size={16} /> Save Evidence</>}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EvidenceCapture;



